import React, { PropsWithChildren, createContext, useContext, useEffect, useState } from 'react'
import { MapInternal } from './Map'
import { MapHeaderProps, MapMarkerProps } from './components/ui'
import { MapBoundsType } from './types'
import { MapContainerProps } from 'react-leaflet'
import { LegendType } from './components/ui/Legend'
import { useRtl } from 'utils/useRtl'
import { MapSettingsType } from './components/ui/MapSettings/types'
import { ExternalSearchProps } from './components/ui/MapSearch/types'
import { LatLngExpression, LatLngTuple } from 'leaflet'
import { ColumnExtended, FilterRequestList } from '@evrekadev/evreka-ui-components'
import { MapPolygonProps } from './components/ui/Polygon'
import { KMLLayerType, KmlLayerItem } from './components/ui/KML'
import { PopupProps } from './components/ui/MapPopup/MapPopup'
import { PolylineProps } from './components/ui/Polyline'
import type {
  MapSettingsFieldGroup,
  MapSettingsKey,
  MapSettingsLocalStorage,
  OnMapSettingsChange,
} from './components/ui/MapSettingsV2/types'

export type MapV2Props = MapHeaderProps &
  MapPolygonProps &
  MapContainerProps & {
    showZoom?: boolean
    showLayers?: boolean
    showSearch?: boolean
    externalSearchProps?: ExternalSearchProps
    shouldFitBoundsToMarkers?: boolean | 'once'
    bounds?: MapBoundsType
    location?: LatLngExpression
    markers?: Array<MapMarkerProps>
    legend?: LegendType | Array<LegendType>
    legendExtraSpace?: boolean
    settings?: MapSettingsType
    onChangeSettings?: (settings: MapSettingsType) => void
    onMapClick?: (e?: LatLngTuple) => void
    setAddress?: React.Dispatch<React.SetStateAction<string>> | ((address?: string) => void)
    shouldGetAddressOnClick?: boolean
    getMapInstance?: (map: L.Map) => void
    onFetch?: (filter: FilterRequestList) => void
    columns?: ColumnExtended<any>[]
    disableInternalParams?: boolean
    customFullscreen?: {
      onFullscreenChange: () => void
      isFullscreen: boolean
    }
    onFitToMarkers?: (map: L.Map) => void
    kmls?: Array<KmlLayerItem>
    onMarkerSelect?: (marker: MapMarkerProps) => void
    markerPopup?: PopupProps
    name?: string
    selectedMarkers?: Array<MapMarkerProps>
    border?: boolean
    noCluster?: boolean
    polylines?: Array<PolylineProps>
    // bulk selection
    showBulkSelection?: boolean
    onBulkSelection?: (markers: Array<MapMarkerProps>) => void
    onToggleBulkSelection?: (active?: boolean) => void
    onClearSelection?: () => void
    'data-testid'?: string
    supercluster?: boolean
    // settings v2
    settingsV2?: {
      values: MapSettingsLocalStorage[MapSettingsKey]
      fields: Array<MapSettingsFieldGroup>
      onChange: OnMapSettingsChange
    }
  }

type MapContextType = MapV2Props & {
  isRtl?: boolean
  isFilterOpen: boolean
  setFilterOpen: React.Dispatch<React.SetStateAction<boolean>>
  parsedKmls?: Array<KMLLayerType>
  setParsedKmls?: React.Dispatch<React.SetStateAction<Array<KMLLayerType>>>
  selectedMarkersSet: Set<string>
}

export const MapContext = createContext<MapContextType>({
  isRtl: false,
  isFilterOpen: false,
  setFilterOpen: () => {},
  selectedMarkersSet: new Set(),
})
export const useMapContext = () => useContext(MapContext)

const MapProvider: React.FC<PropsWithChildren<MapV2Props>> = ({
  children,
  showZoom = true,
  scrollWheelZoom = true,
  showLayers = true,
  selectedMarkers,
  ...props
}) => {
  const isRtl = useRtl()
  const [isFilterOpen, setFilterOpen] = useState(false)
  const [parsedKmls, setParsedKmls] = useState<Array<KMLLayerType>>([])

  const [selectedMarkersSet, setSelectedMarkersSet] = useState<Set<string>>(new Set())

  useEffect(() => {
    setSelectedMarkersSet(new Set(selectedMarkers?.map((marker) => marker.id)))
  }, [selectedMarkers])

  return (
    <MapContext.Provider
      value={{
        isRtl,
        showZoom,
        scrollWheelZoom,
        showLayers,
        isFilterOpen,
        setFilterOpen,
        parsedKmls,
        setParsedKmls,
        selectedMarkers,
        selectedMarkersSet,
        ...props,
      }}
    >
      <MapInternal>{children}</MapInternal>
    </MapContext.Provider>
  )
}

export const Map = MapProvider
